import { useEffect, useState } from "react";
import axios from "axios";
import { API, getAuthHeaders, useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Crown, Star, TrendingUp, Users, CheckCircle, XCircle, ArrowUpCircle, ArrowDownCircle, Pin } from "lucide-react";

/** Master-only widget to set the default referral code applied at signup
 * when the user ticks "I don't have a referral code". */
export function DefaultReferralCodeCard({ compact = false }) {
  const [code, setCode] = useState("");
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/admin/settings/default-referral-code`, { headers: getAuthHeaders() })
      .then(r => {
        setCode(r.data?.code || "");
        setDraft(r.data?.code || "");
      })
      .catch(() => {});
  }, []);

  const save = async () => {
    setError("");
    setSaving(true);
    try {
      const r = await axios.put(
        `${API}/admin/settings/default-referral-code`,
        { code: draft.trim().toUpperCase() },
        { headers: getAuthHeaders() }
      );
      setCode(r.data?.code || "");
      toast.success(`Default referral code set — owner: ${r.data?.owner || "—"}`);
    } catch (err) {
      const d = err.response?.data?.detail || "Failed to save";
      setError(typeof d === "string" ? d : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="default-referral-code-card"
      className={`p-5 rounded-xl border border-white/10 bg-[#13131A]/60 ${compact ? "" : ""}`}
    >
      <div className="flex items-center gap-2 mb-1">
        <Star size={14} className="text-[#7DD3FC]" />
        <h3 className="text-sm font-bold text-white tracking-tight">Default Referral Code</h3>
      </div>
      <p className="text-[11px] text-zinc-500 mb-4">
        Applied automatically when a new signup ticks "I don't have a referral code".
      </p>
      <div className="flex gap-2">
        <input
          data-testid="default-ref-code-input"
          value={draft}
          onChange={(e) => setDraft(e.target.value.toUpperCase())}
          placeholder="FLUXDEMO01"
          className="flex-1 px-3 py-2 rounded-md bg-zinc-950 border border-zinc-800 text-white text-sm font-mono outline-none focus:border-[#7DD3FC]/50"
        />
        <button
          onClick={save}
          disabled={saving || !draft.trim() || draft.trim().toUpperCase() === code}
          data-testid="default-ref-code-save"
          className="px-4 py-2 rounded-md bg-[#7DD3FC] hover:bg-[#BAE6FD] text-[#0A0A0C] text-xs font-semibold disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
      {code && (
        <p className="text-[11px] text-zinc-400 mt-2">
          Current: <span className="font-mono text-[#7DD3FC]">{code}</span>
        </p>
      )}
      {error && <p className="text-[11px] text-rose-400 mt-2">{error}</p>}
    </div>
  );
}

export default function Distributors() {
  const [data, setData] = useState(null);
  const [tier, setTier] = useState("all");
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const isMaster = user?.role === "master_admin";

  const refresh = () => {
    setLoading(true);
    const q = tier === "all" ? "" : `?tier=${tier}`;
    axios.get(`${API}/admin/distributors${q}`, { headers: getAuthHeaders() })
      .then(r => setData(r.data))
      .catch(() => setData({ rows: [], total: 0, sub_distributors: 0, main_distributors: 0 }))
      .finally(() => setLoading(false));
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [tier]);

  const stats = data || { rows: [], total: 0, sub_distributors: 0, main_distributors: 0, thresholds: { sub: 1000, main: 5000 } };

  return (
    <div className="space-y-6" data-testid="distributors-section">
      <DefaultReferralCodeCard />

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard
          testid="distributors-stat-total"
          icon={Users}
          label="Total Distributors"
          value={stats.total}
          hint="Sub + Main combined"
        />
        <StatCard
          testid="distributors-stat-sub"
          icon={Star}
          label="Sub-Distributors"
          value={stats.sub_distributors}
          hint={`≥ ${stats.thresholds?.sub?.toLocaleString() || 1000} referrals`}
          accent="text-sky-300"
        />
        <StatCard
          testid="distributors-stat-main"
          icon={Crown}
          label="Main Distributors"
          value={stats.main_distributors}
          hint={`≥ ${stats.thresholds?.main?.toLocaleString() || 5000} referrals`}
          accent="text-amber-300"
        />
      </div>

      {/* Filter tabs */}
      <div className="flex items-center gap-2">
        {["all", "sub", "main"].map((t) => (
          <button
            key={t}
            data-testid={`distributors-filter-${t}`}
            onClick={() => setTier(t)}
            className={`px-3.5 py-1.5 rounded-full text-xs font-medium transition-colors ${
              tier === t
                ? "bg-[#7DD3FC] text-[#0A0A0C]"
                : "border border-white/10 text-zinc-400 hover:text-white hover:border-white/30"
            }`}
          >
            {t === "all" ? "All" : t === "sub" ? "Sub-Distributors" : "Main Distributors"}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="rounded-xl border border-white/10 bg-[#13131A]/60 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="distributors-table">
            <thead className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 border-b border-white/10">
              <tr>
                <th className="px-4 py-3 text-left">ID</th>
                <th className="px-4 py-3 text-left">Name</th>
                <th className="px-4 py-3 text-left">Code</th>
                <th className="px-4 py-3 text-left">Rank</th>
                <th className="px-4 py-3 text-right">Refs</th>
                <th className="px-4 py-3 text-right">Active</th>
                <th className="px-4 py-3 text-right">Bot Buyers</th>
                <th className="px-4 py-3 text-right">Earnings ₹</th>
                <th className="px-4 py-3 text-left">Bank</th>
                {isMaster && <th className="px-4 py-3 text-left">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={isMaster ? 10 : 9} className="px-4 py-8 text-center text-zinc-500 text-xs">Loading…</td></tr>
              )}
              {!loading && stats.rows.length === 0 && (
                <tr><td colSpan={isMaster ? 10 : 9} className="px-4 py-8 text-center text-zinc-500 text-xs">No distributors yet. Affiliates become Sub-Distributors at {stats.thresholds?.sub?.toLocaleString()} direct referrals.</td></tr>
              )}
              {!loading && stats.rows.map((r) => (
                <tr key={r.user_id} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors" data-testid={`distributor-row-${r.user_id}`}>
                  <td className="px-4 py-3 font-mono text-[11px] text-zinc-400">{r.display_id || "—"}</td>
                  <td className="px-4 py-3">
                    <div className="text-white font-medium">{r.name || "—"}</div>
                    <div className="text-[11px] text-zinc-500">{r.email}</div>
                    {r.phone && <div className="text-[10px] text-zinc-600 font-mono">{r.phone}</div>}
                  </td>
                  <td className="px-4 py-3 font-mono text-[#7DD3FC] text-xs">{r.referral_code}</td>
                  <td className="px-4 py-3">
                    {r.tier === "main_distributor" ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-300 text-[10px] font-semibold">
                        <Crown size={10} /> Main
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-sky-500/10 border border-sky-500/30 text-sky-300 text-[10px] font-semibold">
                        <Star size={10} /> Sub
                      </span>
                    )}
                    {r.tier_manual && (
                      <span data-testid={`distributor-pinned-${r.user_id}`} title={r.tier_manual_reason || "manually pinned"} className="ml-1 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-200 text-[9px] font-bold tracking-wider">
                        <Pin size={8} /> PINNED
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right text-white font-semibold">{r.direct_referrals.toLocaleString()}</td>
                  <td className="px-4 py-3 text-right">
                    <span className="text-emerald-400">{r.active_accounts}</span>
                    <span className="text-zinc-600"> / </span>
                    <span className="text-zinc-500">{r.inactive_accounts}</span>
                  </td>
                  <td className="px-4 py-3 text-right text-[#7DD3FC]">{r.bot_purchasers}</td>
                  <td className="px-4 py-3 text-right font-mono text-emerald-300">
                    ₹{(r.earnings || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </td>
                  <td className="px-4 py-3 text-[11px]" data-testid={`distributor-bank-${r.user_id}`}>
                    {r.bank?.linked ? (
                      <>
                        <div className="text-zinc-200 font-mono">·····{r.bank.account_number_last4}</div>
                        <div className="text-zinc-500 text-[10px]">{r.bank.bank_name || "—"} · {r.bank.ifsc || "—"}</div>
                      </>
                    ) : (
                      <span className="text-rose-400/70 text-[10px]">No bank linked</span>
                    )}
                  </td>
                  {isMaster && (
                    <td className="px-4 py-3" data-testid={`distributor-actions-${r.user_id}`}>
                      <RankActions row={r} onChanged={refresh} />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, hint, accent = "text-white", testid }) {
  return (
    <div data-testid={testid} className="p-5 rounded-xl border border-white/10 bg-[#13131A]/60">
      <div className="flex items-center gap-2 mb-2 text-[10px] tracking-[0.2em] uppercase text-zinc-500">
        <Icon size={12} className={accent} />
        {label}
      </div>
      <div className={`text-3xl font-bold ${accent}`}>{(value ?? 0).toLocaleString()}</div>
      <div className="text-[11px] text-zinc-500 mt-1">{hint}</div>
    </div>
  );
}

function RankActions({ row, onChanged }) {
  const [busy, setBusy] = useState(false);
  const isMain = row.tier === "main_distributor";

  const apply = async (targetTier, label) => {
    const reason = window.prompt(`${label} ${row.name || row.user_id}?\nReason:`, `manual ${label.toLowerCase()}`);
    if (reason === null) return;
    setBusy(true);
    try {
      await axios.put(
        `${API}/admin/users/${row.user_id}/rank-override`,
        { tier: targetTier, reason },
        { headers: getAuthHeaders() }
      );
      toast.success(targetTier ? `Pinned to ${targetTier}` : "Rank override cleared");
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-1">
      {!isMain && (
        <button
          data-testid={`distributor-promote-${row.user_id}`}
          onClick={() => apply("main_distributor", "Promote to Main")}
          disabled={busy}
          className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-semibold bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 text-emerald-300 disabled:opacity-40 transition-colors"
        >
          <ArrowUpCircle size={10} /> Promote
        </button>
      )}
      {isMain && (
        <button
          data-testid={`distributor-demote-${row.user_id}`}
          onClick={() => apply("sub_distributor", "Demote to Sub")}
          disabled={busy}
          className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-semibold bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 text-amber-300 disabled:opacity-40 transition-colors"
        >
          <ArrowDownCircle size={10} /> Demote
        </button>
      )}
      {row.tier_manual && (
        <button
          data-testid={`distributor-clear-${row.user_id}`}
          onClick={() => apply(null, "Clear override")}
          disabled={busy}
          className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-semibold border border-white/10 text-zinc-300 hover:border-white/30 disabled:opacity-40 transition-colors"
        >
          Clear
        </button>
      )}
    </div>
  );
}
